import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// ─── Shared interfaces ────────────────────────────────────────────────────────

export interface FluidReading {
  rpm: number | null;
  rmpVal: number | null;
}

export interface FluidCoeffs {
  n: number;
  k: number;
  ty: number;
}

export interface FluidRheology {
  y: number[];
  t: number[];
  re: number[];
  pl100So: number[];
  plExpectedSo: number[];
}

export interface Fluid {
  title: string;
  density: number | null;
  readingsCount: number;
  readings?: FluidReading[];
  coeffs?: FluidCoeffs;
  rheology?: FluidRheology;
}

export interface SharedSettings {
  holeDiameter: number | null;
  casingOD: number | null;
  standOffOverride: number | null;
  rheologicalOverride: number | null;
}

export interface StandOff {
  innerResults?: { title: string; values: number[] }[];
  max?: number | null;
  rheology?: number[];
  q?: (number | null)[];
  qmin?: number | null;
  qmax?: number | null;
}

export interface SoResults {
  soExpected: StandOff;
  so100: StandOff;
}

// ─── WellGeometry / StandOff Calculator interfaces ────────────────────────────

export interface WellGeometry {
  casingWeight?: number | null;
  casingOD?: number | null;
  casingID?: number | null;
  connectionOD?: number | null;
  holeDiameter?: number | null;
  jointLength?: number | null;
  modulusOfElasticity?: number | null;
  axialTensionLoad?: number | null;
  inclinationAngle?: number | null;
  tailSlurryDensity?: number | null;
  mudDensity?: number | null;
}

export class SOWResults {
  atMiddlePoint?: AtMiddlePoint | null;
  atCentralizers?: AtCentralizers | null;
  lateralLoadResults?: LateralLoadResults | null;
}

export interface AtMiddlePoint {
  inches?: number | null;
  percentage?: number | null;
}

export interface AtCentralizers {
  inches?: number | null;
  percentage?: number | null;
}

export interface LateralLoadResults {
  onCentralizers?: number | null;
  onSpan?: number | null;
}

// ─── SharedSettingsService ────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class SharedSettingsService {
  private settings: SharedSettings = {
    holeDiameter: null,
    casingOD: null,
    standOffOverride: 100,
    rheologicalOverride: 20
  };

  private settingsSubject = new BehaviorSubject<SharedSettings>(this.settings);
  settings$ = this.settingsSubject.asObservable();

  getSharedSetting(): SharedSettings {
    return this.settings;
  }

  setSharedSetting(settings: Partial<SharedSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.settingsSubject.next(this.settings);
  }
}

// ─── StandOffCalcService ──────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class StandOffCalcService {

  calculate(wg: WellGeometry): SOWResults {
    const empty: SOWResults = {
      atMiddlePoint:      { inches: null, percentage: null },
      atCentralizers:     { inches: null, percentage: null },
      lateralLoadResults: { onCentralizers: null, onSpan: null }
    };

    if (Object.values(wg).some(v => v === null)) return empty;

    const rd = Math.PI / 180;

    const we = (wg.casingWeight ?? 0) +
      0.0408 * (
        Math.pow(wg.casingID ?? 0, 2) * (wg.tailSlurryDensity ?? 0) -
        Math.pow(wg.casingOD ?? 0, 2) * (wg.mudDensity ?? 0)
      );

    const ll = (wg.jointLength ?? 0) * 12;
    const mi = (Math.PI / 64) * (Math.pow(wg.casingOD ?? 0, 4) - Math.pow(wg.casingID ?? 0, 4));
    const u  = Math.sqrt(Math.abs(wg.axialTensionLoad ?? 0) * Math.pow(ll, 2) / 4 / (wg.modulusOfElasticity ?? 0) / mi);

    const cosh = (Math.pow(Math.E, u) + Math.pow(Math.E, -u)) / 2;
    const sinh = (Math.pow(Math.E, u) - Math.pow(Math.E, -u)) / 2;

    const lateralLoadOnCentralizers = +((we / 12) * ll * Math.sin((wg.inclinationAngle ?? 0) * rd)).toFixed(2);
    const lateralLoadOnSpan         = +(lateralLoadOnCentralizers / 2).toFixed(2);

    const tempYmax = (lateralLoadOnCentralizers * Math.pow(ll, 3)) / 384 / (wg.modulusOfElasticity ?? 0) / mi;

    let temp: number;
    if (u < 1) {
      temp = (1 + (1 / 15) * Math.pow(u, 2) + (1 / 560) * Math.pow(u, 4)) /
             (1 + (1 / 6)  * Math.pow(u, 2) + (1 / 120) * Math.pow(u, 4));
    } else {
      const check = 0.5 * Math.pow(u, 2) - (u * (cosh - 1)) / sinh;
      temp = check < 0 ? 1 : (24 / Math.pow(u, 4)) * check;
    }

    const deflection = Math.max(tempYmax * temp, 0);
    const rigid      = 0.5 * ((wg.connectionOD ?? 0) - (wg.casingOD ?? 0)) - deflection;

    const halfAnnulus              = 0.5 * ((wg.holeDiameter ?? 0) - (wg.casingOD ?? 0));
    const resAtMiddlePointIn       = rigid < 0 ? 0 : rigid;
    const resAtMiddlePointPercent  = rigid < 0 ? 0 : (resAtMiddlePointIn / halfAnnulus) * 100;
    const resAtCentralizersIn      = Math.max(0.5 * ((wg.connectionOD ?? 0) - (wg.casingOD ?? 0)), 0);
    const resAtCentralizersPercent = (resAtCentralizersIn / halfAnnulus) * 100;

    return {
      atMiddlePoint:      { inches: resAtMiddlePointIn,      percentage: resAtMiddlePointPercent },
      atCentralizers:     { inches: resAtCentralizersIn,     percentage: resAtCentralizersPercent },
      lateralLoadResults: { onCentralizers: lateralLoadOnCentralizers, onSpan: lateralLoadOnSpan }
    } as SOWResults;
  }
}

// ─── RheologyService ──────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class RheologyService {

  constructor(private sharedSettingsSerivce: SharedSettingsService) { }

  private getDefaultReadings(): any {
    return {
      5:  [300, 200, 100, 6, 3],
      6:  [600, 300, 200, 100, 6, 3],
      7:  [300, 200, 100, 60, 30, 6, 3],
      8:  [600, 300, 200, 100, 60, 30, 6, 3],
      10: [600, 300, 200, 100, 60, 30, 20, 10, 6, 3],
      12: [600, 300, 200, 100, 60, 30, 20, 10, 6, 3, 2, 1]
    };
  }

  initFluidPropsObject(title: string, defaultRecordCount: number): Fluid {
    return {
      title,
      density: null,
      readingsCount: defaultRecordCount
    } as Fluid;
  }

  setReadings(fluid: Fluid, readingsCount: number): Fluid {
    const readings = this.getDefaultReadings();
    if (readingsCount != null) {
      fluid.readings = readings[readingsCount];
    }
    return fluid;
  }

  getReadings(readingsCount: number): number[] {
    const readings = this.getDefaultReadings();
    return readings[readingsCount];
  }

  setFluidEquationCoeffs(fluid: Fluid): void {
    const eps = 0.1;
    const goldenRatio = 0.68103399;
    const rpmToReciprocalSeconds = 1.703;
    const dialReadingToApparentViscosity = 1.066;
    const readingsCount = fluid?.readingsCount ?? 0;

    const shearRate   = fluid.readings!.map(v => v.rpm! * rpmToReciprocalSeconds);
    const shearStress = fluid.readings!.map(v => v.rmpVal! * dialReadingToApparentViscosity);

    let ul = shearStress[readingsCount - 1];
    let ll = 0;
    let n1 = 0, n2 = 0, k1 = 0, k2 = 0, yp1 = 0, yp2 = 0;

    const weightedRegression = (yp: number) => {
      let sumW = 0, sumX = 0, sumY = 0, sumXs = 0, sumXy = 0;
      for (let i = readingsCount - 1; i >= 0; i--) {
        const w = shearStress[i] - yp;
        const x = Math.log(Math.abs(shearRate[i]));
        const y = Math.log(Math.abs(w));
        sumW += w; sumX += x * w; sumY += y * w; sumXs += w * x * x; sumXy += w * x * y;
      }
      const n = (sumX * sumY - sumW * sumXy) / (sumX * sumX - sumW * sumXs);
      const k = Math.exp((sumY - n * sumX) / sumW);
      return { n, k };
    };

    const computeSSE = (yp: number, k: number, n: number) => {
      let sse = 0;
      for (let i = readingsCount - 1; i >= 0; i--)
        sse += Math.pow(yp + k * Math.pow(shearRate[i], n) - shearStress[i], 2);
      return sse;
    };

    while (Math.abs(ul - ll) > eps) {
      const diff = (ul - ll) * goldenRatio;
      yp1 = ll + diff;
      yp2 = ul - diff;

      ({ n: n1, k: k1 } = weightedRegression(yp1));
      ({ n: n2, k: k2 } = weightedRegression(yp2));

      const eps1 = computeSSE(yp1, k1, n1);
      const eps2 = computeSSE(yp2, k2, n2);

      if      (eps1 < eps2) ll = yp2;
      else if (eps1 > eps2) ul = yp1;
      else                  { ll = yp1; ul = yp2; }
    }

    fluid.coeffs = {
      n:  (n1 + n2)  / 2,
      k:  (k1 + k2)  / 2,
      ty: (yp1 + yp2) / 2
    };
  }

  calculateFluidRheology(fluid: Fluid): void {
    const flowRate = this.getFlowRate();
    const dialReadingToApparentViscosity = 1.066;
    const n       = fluid?.coeffs?.n  ?? 0;
    const density = fluid?.density    ?? 0;
    const ty      = fluid?.coeffs?.ty ?? 0;
    const k       = fluid?.coeffs?.k  ?? 0;
    const settings = this.sharedSettingsSerivce.getSharedSetting();

    const v = flowRate.map(x =>
      24.51 * 42 * x / (Math.pow(settings.holeDiameter!, 2) - Math.pow(settings.casingOD!, 2))
    );

    const y = v.map(x =>
      1.6 * (2 * n + 1) / (3 * n) * 1.5 * x / (settings.holeDiameter! - settings.casingOD!)
    );

    const t = y.map(x =>
      dialReadingToApparentViscosity * Math.pow(1.5, n) * ty + k * Math.pow(x, n)
    );

    const re = v.map((x1, i) =>
      density * Math.pow(x1, 2) / (19.36 * t[i])
    );

    const fLam = re.map(x => x > 0 ? 16 / x : null);

    const pl100So = v.map((x1, i) =>
      (1.076 * density * Math.pow(x1, 2) * fLam[i]! / ((settings.holeDiameter! - settings.casingOD!) * 100000)) * 1000
    );

    const e = 1 - settings.standOffOverride! / 100;
    const plExpectedSo = re.map((x1, i) => {
      if (x1 > 0) {
        return Math.abs(
          1 - (0.072 * (e / n) * Math.pow(settings.casingOD! / settings.holeDiameter!, 0.8454))
            - (1.5  * Math.pow(e, 2) * Math.pow(n, 0.5) * Math.pow(settings.casingOD! / settings.holeDiameter!, 0.1852))
            + (0.96 * Math.pow(e, 3) * Math.pow(n, 0.5) * Math.pow(settings.casingOD! / settings.holeDiameter!, 0.2527))
        ) * pl100So[i];
      }
      return 0;
    });

    fluid.rheology = { y, t, re, pl100So, plExpectedSo };
  }

  public getFlowRate(): number[] {
    return Array.from({ length: 150 }, (_, i) => (i + 1) * 0.1);
  }

  getRheologyHierarchy(fluids: Fluid[]): SoResults {
    const settings = this.sharedSettingsSerivce.getSharedSetting();
    const flowRate = this.getFlowRate();

    const buildStandOff = (plSoSelector: (f: Fluid) => number[]): StandOff => {
      const so: StandOff = { innerResults: [] };
      const innerArrays: number[][] = [];
      const plSoArrays:  number[][] = [];

      for (const fluid of fluids) {
        const plSo     = plSoSelector(fluid);
        const innerVal = this.getFluidRheologyValues(fluid?.coeffs?.n!, plSo, settings.rheologicalOverride!);
        so.innerResults!.push({ title: fluid.title, values: innerVal });
        innerArrays.push(innerVal);
        plSoArrays.push(plSo);
      }

      const allArrays = [...innerArrays, ...plSoArrays];
      so.max      = this.getMaxFromArrays(allArrays, this.getMaxFromArray);
      so.rheology = this.calculateRheology(plSoArrays, settings.rheologicalOverride!, so.max!);
      so.q        = so.rheology.map((x, i) => x === 0 ? null : flowRate[i]);
      so.qmin     = this.getMinFromArray(so.q as number[]);
      so.qmax     = this.getMaxFromArray(so.q as number[]);
      return so;
    };

    return {
      soExpected: buildStandOff(f => f?.rheology?.plExpectedSo!),
      so100:      buildStandOff(f => f?.rheology?.pl100So!)
    };
  }

  private getFluidRheologyValues(n: number, plSo: number[], rheologyPercent: number): number[] {
    return plSo.map(x => n > 0 ? x * (1 + rheologyPercent / 100) : 0);
  }

  private getMaxFromArrays(arrays: number[][], _: any): number | null {
    const max = Math.max(...arrays.flat().filter((x): x is number => x !== null));
    return max !== -Infinity ? max : null;
  }

  private getMaxFromArray(array: number[]): number | null {
    const max = Math.max(...array.filter((x): x is number => x !== null));
    return max !== -Infinity ? max : null;
  }

  private getMinFromArray(array: number[]): number | null {
    const min = Math.min(...array.filter((x): x is number => x !== null));
    return min !== Infinity ? min : null;
  }

  calculateRheology(plExpectedSoArr: number[][], rheologyPercent: number, maxSo: number): number[] {
    const rheologyK = 1 + rheologyPercent / 100;
    return plExpectedSoArr[0].map((_, n) => {
      const fluidSo = plExpectedSoArr.map(arr => arr[n]);
      let previousSo = 0;
      for (let k = fluidSo.length - 1; k >= 0; k--) {
        if (fluidSo[k] <= 0) continue;
        if (k === fluidSo.length - 1) {
          previousSo = fluidSo[k];
        } else if (previousSo > fluidSo[k] * rheologyK) {
          previousSo = fluidSo[k];
        } else {
          break;
        }
        if (fluidSo.length > 1 && k === 0) return maxSo;
      }
      return 0;
    });
  }
}